import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

interface AmountPercentSliderProps {
  /** Current percentage (0–100). */
  value: number;
  onValueChange: (value: number) => void;
  onMax?: () => void;
  disabled?: boolean;
  /** Tighter spacing for compact modals (Send, Portalize). */
  compact?: boolean;
  className?: string;
}

const TICK_MARKS = [10, 20, 30, 40, 60, 70, 80, 90] as const;

/**
 * Shared amount slider used across portal modals — matches AmountInputModal styling.
 */
const AmountPercentSlider = ({
  value,
  onValueChange,
  onMax,
  disabled = false,
  compact = false,
  className,
}: AmountPercentSliderProps) => {
  const clampedValue = Math.min(100, Math.max(0, value));

  return (
    <div
      className={cn(
        "relative z-10 w-full",
        compact ? "mb-3" : "mb-8",
        className
      )}
    >
      {onMax && (
        <div className={cn("flex justify-end", compact ? "mb-1" : "mb-2")}>
          <button
            type="button"
            onClick={onMax}
            disabled={disabled}
            className="text-[13px] font-medium tracking-wide text-primary transition-colors hover:text-primary/80 disabled:opacity-40"
          >
            Max
          </button>
        </div>
      )}

      <div
        className={cn(
          "relative flex h-8 items-center",
          compact ? "mb-3" : "mb-6"
        )}
      >
        <div className="pointer-events-none absolute inset-0 z-0 flex items-center">
          <div className="relative h-2 w-full">
            <div className="absolute left-0 top-1/2 h-3 w-0.5 -translate-y-1/2 bg-primary" />
            <div className="absolute left-1/4 top-1/2 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-primary" />
            <div className="absolute left-1/2 top-1/2 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-primary" />
            <div className="absolute left-3/4 top-1/2 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-primary" />
            <div className="absolute right-0 top-1/2 h-3 w-0.5 -translate-y-1/2 bg-primary" />
            {TICK_MARKS.map((tick) => (
              <div
                key={tick}
                className="absolute top-1/2 h-2 w-px bg-primary/40"
                style={{ left: `${tick}%`, transform: "translate(-50%, -50%)" }}
              />
            ))}
          </div>
        </div>
        <Slider
          value={[clampedValue]}
          max={100}
          step={1}
          onValueChange={(values) => onValueChange(values[0])}
          disabled={disabled}
          className="relative z-10"
          trackClassName="bg-primary/20"
          rangeClassName="bg-primary"
          thumbClassName="h-4 w-4 border-2 border-primary bg-background shadow-[0_0_10px_hsl(var(--primary)/0.5)]"
        />
      </div>

      <div className={cn("relative flex text-[11px] font-medium tracking-wide", compact ? "-mt-1" : "-mt-4")}>
        <span className="absolute left-0 text-primary">0%</span>
        <span className="absolute left-1/4 -translate-x-1/2 text-primary">25%</span>
        <span className="absolute left-1/2 -translate-x-1/2 text-primary">50%</span>
        <span className="absolute left-3/4 -translate-x-1/2 text-primary">75%</span>
        <span className="absolute right-0 text-primary">100%</span>
      </div>
    </div>
  );
};

export default AmountPercentSlider;
